<template>
  <div class="home" v-loading="loading">
    <!-- Hero 区：品牌渐变 + 搜索入口 -->
    <section class="hero">
      <h1>效率工具，一站直达</h1>
      <p>集成自建工具与优质外链，配合技术文档，帮你更快完成工作</p>
      <div class="hero-search">
        <el-input
          v-model="keyword"
          placeholder="搜索工具名称或描述，回车查看结果"
          size="large"
          clearable
          @keyup.enter="goSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>
      <div class="hero-actions">
        <el-button size="large" class="hero-btn-primary" @click="$router.push('/tools')">
          <el-icon><Tools /></el-icon>&nbsp;浏览工具
        </el-button>
        <el-button size="large" class="hero-btn-ghost" @click="$router.push('/docs')">
          <el-icon><Document /></el-icon>&nbsp;技术文档
        </el-button>
      </div>
    </section>

    <!-- 数据速览 -->
    <section v-if="!loading" class="stats">
      <div class="stat-item">
        <span class="stat-num">{{ categories.length }}</span>
        <span class="stat-label">个分类</span>
      </div>
      <div class="stat-item">
        <span class="stat-num">{{ tools.length }}</span>
        <span class="stat-label">个工具</span>
      </div>
      <div class="stat-item">
        <span class="stat-num">{{ docs.length }}</span>
        <span class="stat-label">篇文档</span>
      </div>
    </section>

    <!-- 分类速览 -->
    <section class="section">
      <div class="section-head">
        <h2>工具分类</h2>
        <el-link type="primary" :underline="false" @click="$router.push('/categories')">
          全部分类<el-icon><ArrowRight /></el-icon>
        </el-link>
      </div>
      <el-row :gutter="16">
        <el-col :xs="12" :sm="8" :md="6" v-for="cat in topCategories" :key="cat.id">
          <el-card class="cat-card" shadow="hover" @click="goCategory(cat.id)">
            <div class="cat-card-inner">
              <el-icon class="cat-icon"><Folder /></el-icon>
              <div class="cat-info">
                <div class="cat-name">{{ cat.name }}</div>
                <div class="cat-count">{{ cat.tool_count || 0 }} 个工具</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <!-- 工具速览 -->
    <section v-if="topTools.length" class="section">
      <div class="section-head">
        <h2>工具速览</h2>
        <el-link type="primary" :underline="false" @click="$router.push('/tools')">
          所有工具<el-icon><ArrowRight /></el-icon>
        </el-link>
      </div>
      <el-row :gutter="16">
        <el-col :xs="12" :sm="8" :md="6" v-for="tool in topTools" :key="tool.id">
          <el-card class="tool-item" shadow="hover" @click="openTool(tool)">
            <div class="tool-item-inner">
              <el-icon class="tool-icon"><Tools /></el-icon>
              <div class="tool-info">
                <div class="tool-name">{{ tool.name }}</div>
                <div class="tool-desc">{{ tool.description || '暂无描述' }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <!-- 最新文档 -->
    <section v-if="latestDocs.length" class="section">
      <div class="section-head">
        <h2>最新文档</h2>
        <el-link type="primary" :underline="false" @click="$router.push('/docs')">
          全部文档<el-icon><ArrowRight /></el-icon>
        </el-link>
      </div>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="8" v-for="doc in latestDocs" :key="doc.id">
          <el-card class="doc-card" shadow="hover" @click="openDoc(doc)">
            <div class="doc-tag" v-if="doc.category">{{ doc.category }}</div>
            <div class="doc-title">{{ doc.title }}</div>
            <div class="doc-desc">{{ doc.description || '暂无描述' }}</div>
            <div class="doc-date">{{ formatDate(doc.updated_at || doc.created_at) }}</div>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <el-empty
      v-if="!loading && !categories.length && !tools.length && !docs.length"
      description="暂无内容，请先在管理后台添加"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search, Tools, Document, Folder, ArrowRight } from '@element-plus/icons-vue'
import { getCategories } from '@/api/categories'
import { getTools } from '@/api/tools'
import { getDocs } from '@/api/docs'
import type { CategoryListItem } from '@/types/category'
import type { Tool } from '@/types/tool'
import type { DocumentItem } from '@/types/document'

const router = useRouter()
const loading = ref(false)
const keyword = ref('')

const categories = ref<CategoryListItem[]>([])
const tools = ref<Tool[]>([])
const docs = ref<DocumentItem[]>([])

const topCategories = computed(() => categories.value.slice(0, 4))
const topTools = computed(() => tools.value.slice(0, 8))
const latestDocs = computed(() => docs.value.slice(0, 3))

onMounted(async () => {
  loading.value = true
  try {
    const [catList, toolList, docList] = await Promise.all([
      getCategories(),
      getTools(),
      getDocs()
    ])
    categories.value = catList
    tools.value = toolList
    docs.value = docList
  } catch (error) {
    console.error('Failed to load home data:', error)
  } finally {
    loading.value = false
  }
})

function goSearch() {
  const q = keyword.value.trim()
  router.push({ path: '/tools', query: q ? { q } : {} })
}

function goCategory(id: number) {
  router.push({ path: '/tools', query: { category: String(id) } })
}

function openTool(tool: Tool) {
  if (tool.tool_type === 'external' && tool.url) {
    window.open(tool.url, '_blank', 'noopener')
  } else if (tool.internal_path) {
    router.push(tool.internal_path)
  } else {
    ElMessage.info('该工具暂未上线，敬请期待')
  }
}

function openDoc(doc: DocumentItem) {
  router.push(`/docs/${doc.id}`)
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('zh-CN')
}
</script>

<style scoped>
.home {
  padding: 0 4px;
}

/* ============ Hero ============ */
.hero {
  background: var(--brand-gradient);
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  color: #fff;
  box-shadow: 0 8px 24px rgba(106, 17, 203, 0.25);
}
.hero h1 {
  margin: 0 0 10px;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 1px;
}
.hero p {
  margin: 0 0 24px;
  font-size: 15px;
  opacity: 0.9;
}
.hero-search {
  max-width: 520px;
  margin: 0 auto 20px;
}
.hero-search :deep(.el-input__wrapper) {
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  padding: 4px 8px;
}
.hero-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}
.hero-btn-primary,
.hero-btn-ghost {
  border: none;
  border-radius: 999px;
  padding: 0 28px;
  font-weight: 600;
}
.hero-btn-primary {
  background: #fff;
  color: var(--el-color-primary);
}
.hero-btn-primary:hover {
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary-dark-2);
  border-color: transparent;
}
.hero-btn-ghost {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}
.hero-btn-ghost:hover {
  background: rgba(255, 255, 255, 0.24);
  color: #fff;
  border-color: transparent;
}

/* ============ 数据速览 ============ */
.stats {
  display: flex;
  justify-content: center;
  gap: 48px;
  margin: 28px 0 8px;
  flex-wrap: wrap;
}
.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stat-num {
  font-size: 30px;
  font-weight: 700;
  color: var(--el-color-primary);
  line-height: 1.2;
}
.stat-label {
  font-size: 13px;
  color: #909399;
}

/* ============ 区块 ============ */
.section {
  margin-top: 32px;
}
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.section-head h2 {
  margin: 0;
  font-size: 18px;
  color: #303133;
}
.section-head .el-link {
  font-size: 14px;
}
.section-head .el-link .el-icon {
  margin-left: 2px;
  vertical-align: -2px;
}

/* 分类卡 */
.cat-card {
  cursor: pointer;
  margin-bottom: 16px;
  border-radius: 10px;
  transition: transform 0.25s, box-shadow 0.25s;
}
.cat-card:hover {
  transform: translateY(-2px);
}
.cat-card-inner {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cat-icon {
  font-size: 26px;
  color: var(--el-color-primary);
  flex-shrink: 0;
}
.cat-name {
  font-weight: 600;
  color: #303133;
}
.cat-count {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

/* 工具卡 */
.tool-item {
  cursor: pointer;
  margin-bottom: 16px;
  border-radius: 10px;
  transition: transform 0.25s, box-shadow 0.25s;
}
.tool-item:hover {
  transform: translateY(-2px);
}
.tool-item-inner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.tool-icon {
  font-size: 22px;
  color: var(--el-color-primary);
  flex-shrink: 0;
  margin-top: 2px;
}
.tool-name {
  font-weight: 600;
  color: #303133;
  font-size: 15px;
}
.tool-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 文档卡 */
.doc-card {
  cursor: pointer;
  margin-bottom: 16px;
  border-radius: 10px;
  transition: transform 0.25s, box-shadow 0.25s;
}
.doc-card:hover {
  transform: translateY(-2px);
}
.doc-tag {
  display: inline-block;
  font-size: 12px;
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
  border-radius: 4px;
  padding: 2px 8px;
  margin-bottom: 8px;
}
.doc-title {
  font-weight: 600;
  color: #303133;
  font-size: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.doc-desc {
  font-size: 13px;
  color: #909399;
  margin: 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.doc-date {
  font-size: 12px;
  color: #c0c4cc;
}

@media (max-width: 768px) {
  .hero {
    padding: 32px 16px;
    border-radius: 12px;
  }
  .hero h1 {
    font-size: 1.5rem;
  }
  .stats {
    gap: 28px;
  }
}
</style>
